/**
 * EmployeeDutyService
 * Service for EmployeeDuty entity operations (composite key: UserId + DutyID)
 * Route: api/employeeduties
 */
import { BaseService } from '../base/BaseService';
/**
 * EmployeeDutyService
 * Handles employee duty operations with composite key support
 */
export class EmployeeDutyService extends BaseService {
    constructor(http, config) {
        const internalConfig = {
            servicePath: config?.servicePath ?? EmployeeDutyService.DEFAULT_SERVICE_PATH,
            basePath: EmployeeDutyService.BASE_PATH,
            endpoints: config?.endpoints,
        };
        super(http, internalConfig);
    }
    // ==================== GET ALL ====================
    /**
     * Get all employee duties
     * GET api/employeeduties
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all employee duties with full response
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    // ==================== GET BY COMPOSITE KEY ====================
    /**
     * Get employee duty by composite key (userId and dutyId)
     * GET api/employeeduties/{userId}/{dutyId}
     */
    async getByCompositeKey(userId, dutyId) {
        const response = await this.getByCompositeKeyRaw(userId, dutyId);
        if (!response.data) {
            throw new Error(`Employee duty ${userId}/${dutyId} not found`);
        }
        return response.data;
    }
    /**
     * Get employee duty by composite key with full response
     */
    getByCompositeKeyRaw(userId, dutyId) {
        const url = this.buildUrl(`${userId}/${dutyId}`);
        return this.http.get(url);
    }
    // ==================== GET BY USER ID ====================
    /**
     * Get all duties for a specific employee
     * GET api/employeeduties/by-user/{userId}
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get all duties for a specific employee with full response
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('by-user', `by-user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET BY DUTY ID ====================
    /**
     * Get all employees assigned to a specific duty
     * GET api/employeeduties/by-duty/{dutyId}
     */
    async getByDutyId(dutyId) {
        const response = await this.getByDutyIdRaw(dutyId);
        return response.data ?? [];
    }
    /**
     * Get all employees assigned to a specific duty with full response
     */
    getByDutyIdRaw(dutyId) {
        const endpoint = this.getEndpoint('by-duty', `by-duty/${dutyId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new employee duty
     * POST api/employeeduties
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create employee duty');
        }
        return response.data;
    }
    /**
     * Create a new employee duty with full response
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing employee duty
     * PUT api/employeeduties
     */
    async update(data) {
        const response = await this.updateRaw(data);
        if (!response.data) {
            throw new Error('Failed to update employee duty');
        }
        return response.data;
    }
    /**
     * Update an existing employee duty with full response
     */
    updateRaw(data) {
        const url = this.buildUrl();
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete an employee duty by composite key
     * DELETE api/employeeduties/{userId}/{dutyId}
     */
    async deleteByCompositeKey(userId, dutyId) {
        const response = await this.deleteByCompositeKeyRaw(userId, dutyId);
        return response.success;
    }
    /**
     * Delete an employee duty by composite key with full response
     */
    deleteByCompositeKeyRaw(userId, dutyId) {
        const url = this.buildUrl(`${userId}/${dutyId}`);
        return this.http.delete(url);
    }
}
EmployeeDutyService.DEFAULT_SERVICE_PATH = 'hr';
EmployeeDutyService.BASE_PATH = 'api/employeeduties';
//# sourceMappingURL=EmployeeDutyService.js.map